// frontend/src/pages/AdminAnalytics.jsx
import React, { useEffect, useMemo, useState, useContext } from "react";
import "./AdminAnalytics.css";
import { AuthContext } from "../context/AuthContext";
import { authHeaders } from "../utils/authHeaders";

const API = process.env.REACT_APP_API_URL || "/api";

function formatDuration(rawSeconds) {
  const total = Math.max(0, Math.round(Number(rawSeconds) || 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;

  if (hours) return `${hours}j ${minutes}m`;
  if (minutes) return `${minutes}m ${seconds}d`;
  return `${seconds}d`;
}

function formatChartDate(day) {
  if (!day) return "";
  return new Date(day).toLocaleDateString("id-ID", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/* ======= Line chart sederhana (tanpa area) ======= */
function LineChart({ data = [], valueKey = "value", height = 120 }) {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const width = Math.max(280, data.length * 28);
  const pad = 16;

  const vals = useMemo(
    () => data.map((d) => Number(d?.[valueKey] || 0)),
    [data, valueKey]
  );
  const yMax = useMemo(() => (vals.length ? Math.max(...vals) : 0), [vals]);

  const points = useMemo(() => {
    if (!data.length) return "";
    const maxV = Math.max(1, ...vals);
    const xStep = (width - pad * 2) / Math.max(1, data.length - 1);
    return vals
      .map((v, i) => {
        const x = pad + i * xStep;
        const y = pad + (height - pad * 2) * (1 - v / maxV);
        return `${x},${y}`;
      })
      .join(" ");
  }, [data, vals, height, width]);

  const dots = useMemo(() => {
    if (!data.length) return [];
    const maxV = Math.max(1, ...vals);
    const xStep = (width - pad * 2) / Math.max(1, data.length - 1);
    return vals.map((v, i) => {
      const x = pad + i * xStep;
      const y = pad + (height - pad * 2) * (1 - v / maxV);
      return { x, y, v, day: data[i]?.day };
    });
  }, [data, vals, height, width]);

  const hovered = hoveredIndex === null ? null : dots[hoveredIndex];
  const hoveredDate = formatChartDate(hovered?.day);

  return (
    <div className="chart-wrap" onMouseLeave={() => setHoveredIndex(null)}>
      <svg width="100%" viewBox={`0 0 ${width} ${height}`} aria-label="Line chart">
        {/* axes */}
        <line x1={pad} y1={height - pad} x2={width - pad} y2={height - pad} className="axis"/>
        <line x1={pad} y1={pad} x2={pad} y2={height - pad} className="axis"/>

        {/* garis */}
        {points && (
          <polyline className="line" fill="none" strokeWidth="2" points={points} />
        )}

        {/* titik */}
        {dots.map((d, idx) => (
          <g key={idx}>
            <circle cx={d.x} cy={d.y} r="2.8" className="dot" />
            <circle
              cx={d.x}
              cy={d.y}
              r="10"
              className="dot-hit"
              tabIndex="0"
              role="button"
              aria-label={`${formatChartDate(d.day) || "Data"}: ${d.v}`}
              onMouseEnter={() => setHoveredIndex(idx)}
              onFocus={() => setHoveredIndex(idx)}
              onBlur={() => setHoveredIndex(null)}
              onClick={() => setHoveredIndex(idx)}
            />
          </g>
        ))}
      </svg>
      {hovered && (
        <div
          className="chart-tooltip"
          style={{
            left: `${(hovered.x / width) * 100}%`,
            top: `${(hovered.y / height) * 100}%`,
          }}
        >
          {hoveredDate && <span>{hoveredDate}</span>}
          <strong>{hovered.v}</strong>
        </div>
      )}
      <div className="chart-foot">
        <span>max: {yMax}</span>
      </div>
    </div>
  );
}

/* ======= Halaman ======= */
export default function AdminAnalytics() {
  const { user } = useContext(AuthContext);
  const isAdmin = (user?.role || "").toLowerCase() === "admin";

  const [from, setFrom] = useState(() => {
    const d = new Date(Date.now() - 6 * 24 * 3600 * 1000);
    return d.toISOString().slice(0, 10);
  });
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));

  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [series, setSeries] = useState({
    pageviews: [],
    visits: [],
    visitors: [],
  });
  const [topPages, setTopPages] = useState([]);
  const [referrers, setReferrers] = useState([]);
  const [geo, setGeo] = useState([]);
  const [engagement, setEngagement] = useState({
    totals: {},
    daily: [],
    pages: [],
  });
  const [error, setError] = useState("");

  const headers = useMemo(
    () => authHeaders(user),
    [user]
  );

  async function fetchAll() {
    setLoading(true);
    setError("");
    try {
      const q = `from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
      const [sumRes, pvRes, vsRes, vrRes, topRes, refRes, geoRes, engRes] =
        await Promise.all([
          fetch(`${API}/analytics/admin/summary?${q}`, { headers }),
          fetch(`${API}/analytics/admin/timeseries?metric=pageviews&${q}`, { headers }),
          fetch(`${API}/analytics/admin/timeseries?metric=visits&${q}`, { headers }),
          fetch(`${API}/analytics/admin/timeseries?metric=visitors&${q}`, { headers }),
          fetch(`${API}/analytics/admin/top-pages?${q}`, { headers }),
          fetch(`${API}/analytics/admin/referrers?${q}`, { headers }),
          fetch(`${API}/analytics/admin/geo?${q}`, { headers }),
          fetch(`${API}/analytics/admin/engagement?${q}`, { headers }),
        ]);

      const [sum, pv, vs, vr, top, ref, g, eng] = await Promise.all([
        sumRes.json(),
        pvRes.json(),
        vsRes.json(),
        vrRes.json(),
        topRes.json(),
        refRes.json(),
        geoRes.json(),
        engRes.json(),
      ]);

      if (!sum?.success) throw new Error("Gagal ambil summary");

      setSummary(sum);
      setSeries({
        pageviews: pv?.data || [],
        visits: vs?.data || [],
        visitors: vr?.data || [],
      });
      setTopPages(top?.data || []);
      setReferrers(ref?.data || []);
      setGeo(g?.data || []);
      setEngagement({
        totals: eng?.totals || {},
        daily: eng?.daily || [],
        pages: eng?.pages || [],
      });
    } catch (e) {
      console.error(e);
      setError("Gagal memuat data analitik.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (isAdmin) fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, isAdmin]);

  if (!isAdmin) {
    return (
      <div className="an-wrap">
        <div className="an-denied">Hanya admin yang dapat melihat halaman ini.</div>
      </div>
    );
  }

  const totals = summary?.totals || { pageviews: 0, visits: 0, visitors: 0 };
  const engagementTotals = engagement.totals || {};
  const visitorSeries = engagement.daily.map((row) => ({
    day: row.day,
    value: row.visitors,
  }));
  const sessionSeries = engagement.daily.map((row) => ({
    day: row.day,
    value: row.sessions,
  }));

  return (
    <div className="an-wrap">
      {/* STICKY HEAD */}
      <div className="an-head an-sticky">
        <h1>Dashboard Pengunjung</h1>
        <div className="an-range">
          <label>
            <span>Dari</span>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label>
            <span>Sampai</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
          <button className="an-btn" onClick={fetchAll} disabled={loading}>
            {loading ? "Memuat..." : "Refresh"}
          </button>
        </div>
      </div>

      {error && <div className="an-error">{error}</div>}

      {/* Summary cards */}
      <div className="an-cards">
        <div className="an-card">
          <div className="an-card-title">Pageviews</div>
          <div className="an-card-num">{totals.pageviews ?? 0}</div>
          <LineChart data={series.pageviews} valueKey="value" />
        </div>
        <div className="an-card">
          <div className="an-card-title">Pengunjung Website</div>
          <div className="an-card-num">{engagementTotals.visitors ?? 0}</div>
          <LineChart data={visitorSeries} valueKey="value" />
        </div>
        <div className="an-card">
          <div className="an-card-title">Sessions</div>
          <div className="an-card-num">{engagementTotals.sessions ?? 0}</div>
          <LineChart data={sessionSeries} valueKey="value" />
        </div>
        <div className="an-card">
          <div className="an-card-title">Rata-rata Engagement</div>
          <div className="an-card-num">
            {formatDuration(engagementTotals.avg_engagement_seconds)}
          </div>
          <div className="an-card-note">Waktu aktif per session</div>
        </div>
      </div>

      <div className="an-metrics">
        <div className="an-metric">
          <span>Returning visitors</span>
          <strong>{engagementTotals.returning_visitors ?? 0}</strong>
        </div>
        <div className="an-metric">
          <span>Engagement retention</span>
          <strong>{engagementTotals.retention_rate ?? 0}%</strong>
        </div>
        <div className="an-metric">
          <span>Halaman per session</span>
          <strong>{engagementTotals.avg_pages_per_session ?? 0}</strong>
        </div>
        <div className="an-metric">
          <span>Total engagement</span>
          <strong>{formatDuration(engagementTotals.engagement_seconds)}</strong>
        </div>
      </div>

      <div className="an-panel an-section">
        <div className="an-panel-head">
          <h3>Halaman yang Dikunjungi</h3>
        </div>
        <div className="an-table-wrap">
          <table className="an-table">
            <thead>
              <tr>
                <th>Halaman</th>
                <th>Pageviews</th>
                <th>Pengunjung</th>
                <th>Sessions</th>
                <th>Avg. engagement</th>
              </tr>
            </thead>
            <tbody>
              {engagement.pages.length === 0 ? (
                <tr><td colSpan={5} className="empty">Belum ada data</td></tr>
              ) : engagement.pages.map((page) => (
                <tr key={page.page}>
                  <td className="mono">{page.page}</td>
                  <td>{page.pageviews}</td>
                  <td>{page.visitors}</td>
                  <td>{page.sessions}</td>
                  <td>{formatDuration(page.avg_engagement_seconds)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="an-panel an-section">
        <div className="an-panel-head">
          <h3>Engagement Retention Harian</h3>
        </div>
        <div className="an-table-wrap">
          <table className="an-table">
            <thead>
              <tr>
                <th>Tanggal</th>
                <th>Pengunjung</th>
                <th>Returning</th>
                <th>Retention</th>
                <th>Engagement</th>
              </tr>
            </thead>
            <tbody>
              {engagement.daily.length === 0 ? (
                <tr><td colSpan={5} className="empty">Belum ada data</td></tr>
              ) : engagement.daily.map((day) => (
                <tr key={day.day}>
                  <td>{new Date(day.day).toLocaleDateString("id-ID")}</td>
                  <td>{day.visitors}</td>
                  <td>{day.returning_visitors}</td>
                  <td>{day.retention_rate}%</td>
                  <td>{formatDuration(day.engagement_seconds)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top pages & Referrers */}
      <div className="an-grid">
        <div className="an-panel">
          <div className="an-panel-head">
              <h3>Semua Halaman</h3>
          </div>
          <div className="an-table-wrap">
            <table className="an-table">
              <thead>
                <tr>
                  <th>Page</th>
                  <th>Pageviews</th>
                  <th>Visits</th>
                  <th>Visitors</th>
                </tr>
              </thead>
              <tbody>
                {topPages.length === 0 ? (
                  <tr><td colSpan={4} className="empty">Belum ada data</td></tr>
                ) : topPages.map((p) => (
                  <tr key={p.page}>
                    <td className="mono">{p.page}</td>
                    <td>{p.pageviews}</td>
                    <td>{p.visits}</td>
                    <td>{p.visitors}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="an-panel">
          <div className="an-panel-head">
            <h3>Top Referrers</h3>
          </div>
          <div className="an-table-wrap">
            <table className="an-table">
              <thead>
                <tr>
                  <th>Referrer</th>
                  <th>Events</th>
                </tr>
              </thead>
              <tbody>
                {referrers.length === 0 ? (
                  <tr><td colSpan={2} className="empty">Belum ada data</td></tr>
                ) : referrers.map((r) => (
                  <tr key={r.referrer}>
                    <td className="mono">{r.referrer}</td>
                    <td>{r.events}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Geo table */}
      <div className="an-panel">
        <div className="an-panel-head">
          <h3>Asal Pengunjung (Geo)</h3>
        </div>
        <div className="an-table-wrap">
          <table className="an-table">
            <thead>
              <tr>
                <th>Provinsi</th>
                <th>Kabupaten/Kota</th>
                <th>Kecamatan</th>
                <th>Events</th>
              </tr>
            </thead>
            <tbody>
              {geo.length === 0 ? (
                <tr><td colSpan={4} className="empty">Belum ada data</td></tr>
              ) : geo.map((g, idx) => (
                <tr key={`${g.region}-${g.kabupaten}-${g.kecamatan}-${idx}`}>
                  <td>{g.region || "-"}</td>
                  <td>{g.kabupaten || "-"}</td>
                  <td>{g.kecamatan || "-"}</td>
                  <td>{g.events}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
